import { isWeb } from "@stepapp/core";
import {
  annaProfile,
  Block,
  Divider,
  Image,
  ImageBackground,
  palette,
  SvgIcon,
  spacing,
  Typography,
  useBreakpoints,
  workerAnna,
} from "@stepapp/ui";
import { useTranslation } from "react-i18next";
import { StyleSheet } from "react-native";

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: skip
const ClientSafety = () => {
  const { t } = useTranslation();
  const { sm, md } = useBreakpoints();

  const height = spacing.unit * (md ? 75 : sm ? 50 : 34);

  const items = [
    {
      label: t("screens.dashboard.reliability"),
      description: t("screens.dashboard.reliabilityDescription"),
      icon: "trophy-outline",
    },
    {
      label: t("screens.dashboard.hygiene"),
      description: t("screens.dashboard.hygieneDescription"),
      icon: "starOfLife",
    },
    {
      label: t("screens.dashboard.insurance"),
      description: t("screens.dashboard.insuranceDescription"),
      icon: "verified-big",
    },
  ] as const;

  return (
    <>
      <Block style={styles.container} row={md} marginBottom={md ? 16 : 7} marginTop={md ? 13 : 5}>
        <ImageBackground
          contentFit={md ? "fill" : "cover"}
          contentPosition={{ top: md ? 0 : -20 }}
          source={workerAnna}
          style={[
            styles.imageContainer,
            { height },
            md ? { flex: 1 } : { paddingHorizontal: spacing.unit * 4.5 },
          ]}
        >
          <Block
            row
            width={md ? 33 : 20}
            paddingHorizontal={md ? 3 : 1.5}
            paddingVertical={md ? 1.25 : 0.75}
            style={[styles.workerContainer, { top: md ? -38 : -22 }]}
          >
            <Image
              contentFit="contain"
              height={md ? 55 : 33}
              source={annaProfile}
              style={styles.workerImage}
              width={md ? 55 : 33}
            />
            <Block marginLeft={md ? 2 : 1}>
              <Typography variant={md ? "body" : "smallLabel"}>Anna</Typography>
              <Block
                row
                height={md ? 2.5 : 2}
                style={md ? styles.starContainerLarge : styles.starContainer}
              >
                {[...new Array(5)].map((_, i) => (
                  <SvgIcon
                    // biome-ignore lint/suspicious/noArrayIndexKey: reason
                    key={`star-${i}`}
                    color={palette.primary.pinkSalmon}
                    name="star"
                    style={!md && styles.star}
                    size={md ? 18 : 12}
                  />
                ))}
              </Block>
            </Block>
          </Block>

          <Block
            row
            width={md ? 48 : 34}
            paddingLeft={md ? 3 : 1.5}
            paddingVertical={md ? 2 : 1}
            style={styles.verifiedAndTrained}
          >
            <SvgIcon
              color={palette.primary.pinkSalmon}
              name="shield-star"
              size={md ? 25 : 18}
              style={styles.verifiedAndTrainedIcon}
            />
            <Typography type="white" variant={md ? "paragraphBold" : "navItem"}>
              {t("screens.dashboard.verifiedAndTrained")}
            </Typography>
          </Block>
        </ImageBackground>

        <Block flex={md ? 1 : undefined} style={styles.safetyContent} marginTop={md ? 0 : 3}>
          <Typography
            center={!md}
            variant={md ? "largeTitle" : "newH2"}
            style={{ fontSize: md ? 34 : 25 }}
          >
            {t("screens.dashboard.yourSafetyPriority")}
          </Typography>

          {items.map((item) => (
            <Block marginTop={md ? 4 : 3} row={md} key={item.label}>
              <SvgIcon style={styles.iconContainer} name={item.icon} size={22} />

              <Block
                flex={isWeb ? 1 : undefined}
                style={[
                  styles.textContainer,
                  md ? { marginLeft: spacing.unit * 3.5 } : { marginTop: spacing.unit * 1 },
                ]}
              >
                <Typography center={!md} variant="bodyBold" style={styles.itemTitle}>
                  {item.label}
                </Typography>

                <Typography
                  center={!md}
                  variant="paragraph"
                  style={[styles.itemDescription, { marginTop: spacing.unit * (md ? 1.25 : 0.5) }]}
                >
                  {item.description}
                </Typography>
              </Block>
            </Block>
          ))}
        </Block>
      </Block>
      <Divider color={palette.common.mediumGray} spaceless style={styles.divider} />
    </>
  );
};

export default ClientSafety;

const styles = StyleSheet.create({
  container: {
    maxWidth: spacing.unit * 125,
    width: "100%",
    margin: "auto",
    gap: spacing.unit * 6,
  },
  divider: {
    marginBottom: spacing.unit * 7,
  },
  imageContainer: {
    position: "relative",
  },
  safetyContent: {
    justifyContent: "center",
  },
  iconContainer: {
    minWidth: 38,
    alignSelf: "center",
  },
  textContainer: {
    flexShrink: 1,
  },
  itemTitle: {
    flexShrink: 1,
    flexWrap: "wrap",
  },
  itemDescription: {
    flexShrink: 1,
    flexWrap: "wrap",
  },
  verifiedAndTrained: {
    backgroundColor: palette.primary.accent,
    borderRadius: spacing.radius,
    borderWidth: 1,
    borderColor: palette.font.secondary,
    position: "absolute",
    alignSelf: "center",
    boxShadow: "0 4px 4px 0 rgba(0, 0, 0, 0.25)",
    bottom: -30,
  },
  verifiedAndTrainedIcon: {
    alignSelf: "center",
    marginRight: spacing.unit * 0.5,
    width: 66,
  },
  workerContainer: {
    backgroundColor: palette.common.white,
    borderRadius: spacing.radius,
    borderWidth: 1,
    position: "absolute",
    alignSelf: "center",
  },
  workerImage: {
    borderRadius: spacing.radius * 10,
  },
  starContainerLarge: {
    marginLeft: -spacing.unit * 0.5,
  },
  starContainer: {
    marginTop: -6,
    marginLeft: 2,
  },
  star: {
    marginLeft: -8,
  },
});
